import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { Card, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SaveOutlined } from '@ant-design/icons';
import {
  PrintDestinationType,
  ReceiptPrinterType,
  RegisterStatus,
  ShipmentLabelPrinterType,
  TicketPrinterType,
  TransportRoleType,
} from '@/constants';
import StationInput from '@/components/FormItems/StationInput';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { useGetAllGlobalUsers } from '@/hooks/data/masterDataHook';
import { getFullName } from '@/helpers/string';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: (reload?: boolean) => void;
  registerId?: string;
};

export default function CreateOrUpdateStationModal({ onClose, registerId }: Props) {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const { data } = useGetAllGlobalUsers(TransportRoleType.GlobalCashier);

  React.useEffect(() => {
    if (registerId) {
      setLoading(true);
      apiSdk.RegisterService.getRegisterDetails({ id: registerId })
        .then(res => {
          if (res) {
            form.setFieldsValue({
              ...res,
              registerCashiersUserIds: res.cashierIds,
            });
          }
        })
        .catch(defaultApiErrorHandle)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [registerId]);

  const onCreate = (values: any) => {
    setLoading(true);
    apiSdk.RegisterService.create({
      body: values,
    })
      .then(response => {
        if (response?.success) {
          onClose(true);
          if (response.message) {
            Modal.warning({
              title: t(response.message),
            });
          }
        } else {
          defaultApiErrorHandle(response);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };

  const onUpdate = (values: any) => {
    setLoading(true);
    apiSdk.RegisterService.update({
      body: {
        id: registerId,
        ...values,
      },
    })
      .then(response => {
        if (response?.success) {
          onClose(true);
          if (response.message) {
            Modal.warning({
              title: t(response.message),
            });
          }
        } else {
          defaultApiErrorHandle(response);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      maskClosable={false}
      width={ModalWidth.extraLarge}
      className="nta-custom-form"
      title={registerId ? t('registerPage_UpdateRegister') : t('registerPage_CreateRegister')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={() => {
        form.resetFields();
        onClose(false);
      }}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            if (registerId) {
              onUpdate(values);
            } else {
              onCreate(values);
            }
          })
          .catch(() => {});
      }}
    >
      <Form
        form={form}
        size="middle"
        layout="horizontal"
        labelAlign="right"
        labelCol={{ md: 10, xs: 24 }}
        initialValues={
          {
            registerStatus: RegisterStatus.Ready,
            printDestination: PrintDestinationType.Bixolon,
            startingCash: 0,
          } as any
        }
      >
        <Row gutter={ROW_GUTTER}>
          <Col md={12}>
            <Card className="mtopo-card" size="small" title={t('registerPage_RegisterInfo')}>
              <Form.Item label={t('registerPage_Name')} name="registerName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('registerPage_StationName')} name="stationId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <StationInput />
              </Form.Item>
              {registerId ? null : (
                <Form.Item label={t('registerPage_RegisterStatus')} name="registerStatus" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select options={RegisterStatus.__getCreateRegisterSelectOptions()} />
                </Form.Item>
              )}

              <Form.Item label={t('registerPage_OpeningHoursFrom')} name="openingHoursFrom" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input type="time" className="w-100" />
              </Form.Item>
              <Form.Item label={t('registerPage_OpeningHoursTo')} name="openingHoursTo" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input type="time" className="w-100" />
              </Form.Item>
              <Form.Item label={t('registerPage_StartingCash')} name="startingCash" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item label={t('registerPage_PrintDestination')} name="printDestination">
                <Select options={PrintDestinationType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('registerPage_ReceiptDefaultPrinter')} name="receiptDefaultPrinter">
                <Select options={ReceiptPrinterType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('registerPage_TicketDefaultPrinter')} name="ticketDefaultPrinter">
                <Select options={TicketPrinterType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('registerPage_ShipmentLabelDefaultPrinter')} name="shipmentLabelDefaultPrinter">
                <Select options={ShipmentLabelPrinterType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('registerPage_loyaltyClient')} name="loyaltyClient" valuePropName="checked">
                <Checkbox />
              </Form.Item>
              <Form.Item label={t('registerPage_ActionAnnulation')} name="actionAnnulation" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Card>
          </Col>
          <Col md={12}>
            <Card className="mtopo-card" size="small" title={t('registerPage_Cashiers')}>
              <Form.Item name="registerCashiersUserIds">
                <Checkbox.Group>
                  <Row gutter={ROW_GUTTER}>
                    {data?.map(user => {
                      return (
                        <Col span={24} key={user.id}>
                          <Checkbox value={user.id}>{getFullName(user.firstName, user.lastName)}</Checkbox>
                        </Col>
                      );
                    })}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
